import { useState, useEffect } from "react";
import styles from "./Install.module.css";
import {
    getInstallPrompt,
    onInstallPromptChange,
    clearInstallPrompt,
    isIos,
    isInStandaloneMode,
    type BeforeInstallPromptEvent,
} from "../../services/pwaInstall";
import { brand } from "@brand";

export const Install = () => {
    const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(getInstallPrompt);
    const standalone = isInStandaloneMode();
    const ios = isIos();

    useEffect(() => onInstallPromptChange((e) => setInstallEvent(e)), []);

    const handleInstall = async () => {
        if (!installEvent) return;
        await installEvent.prompt();
        const { outcome } = await installEvent.userChoice;
        if (outcome === "accepted") clearInstallPrompt();
    };

    return (
        <main className={styles.pages}>
            <h1>Install {brand.name}</h1>

            {standalone ? (
                <section>
                    <div className={styles.availability}>Installed</div>
                    <p>You're running {brand.name} as an installed app.</p>
                </section>
            ) : (
                <section>
                    <p className={styles.subtitle}>
                        Add {brand.name} to your home screen for quick, app-like access — no app store needed.
                    </p>

                    <div className={styles.columnBox}>
                        {ios ? (
                            <>
                                <p style={{ margin: 0, lineHeight: 1.6 }}>
                                    To install {brand.name} on your device:
                                </p>
                                <ol style={{ marginTop: 12, paddingLeft: 20, lineHeight: 2 }}>
                                    <li>Open this page in <strong>Safari</strong> (required for iOS)</li>
                                    <li>Tap the <strong>Share</strong> button <span style={{ fontSize: 20 }}>⎋</span> at the bottom of the screen</li>
                                    <li>Scroll down and tap <strong>Add to Home Screen</strong></li>
                                    <li>Tap <strong>Add</strong> in the top right</li>
                                </ol>
                                <p style={{ marginTop: 12, fontSize: 13, color: "var(--neutral-Secondary)" }}>
                                    Note: On iOS, only Safari supports installing web apps. Chrome, Firefox, and other browsers on iPhone/iPad cannot install PWAs.
                                </p>
                            </>
                        ) : installEvent ? (
                            <>
                                <p style={{ margin: "0 0 16px 0", lineHeight: 1.6 }}>
                                    Tap the button below to install {brand.name} on your device.
                                </p>
                                <button onClick={handleInstall} style={installBtnStyle}>
                                    Install {brand.name}
                                </button>
                            </>
                        ) : (
                            <>
                                <p style={{ margin: 0, lineHeight: 1.6 }}>
                                    To install {brand.name}, open your browser's menu and look for <strong>"Install app"</strong> or <strong>"Add to Home Screen"</strong>.
                                </p>
                            </>
                        )}
                    </div>
                </section>
            )}

            <section>
                <h2>Why Install?</h2>
                <div className={styles.twoColumn}>
                    <div className={styles.columnBox}>
                        <h3 className={styles.columnTitle}>Quick Access</h3>
                        <p style={{ margin: 0 }}>Launch {brand.name} directly from your home screen — no need to open a browser and type in the URL.</p>
                    </div>
                    <div className={styles.columnBox}>
                        <h3 className={styles.columnTitle}>Full Screen</h3>
                        <p style={{ margin: 0 }}>Runs in full-screen mode without browser navigation bars, giving you more screen space.</p>
                    </div>
                </div>
            </section>
        </main>
    );
};

const installBtnStyle: React.CSSProperties = {
    padding: "12px 32px",
    borderRadius: 8,
    border: "none",
    background: "var(--theme-Primary, #4a7aff)",
    color: "#fff",
    fontWeight: 600,
    fontSize: 16,
    cursor: "pointer",
    fontFamily: "inherit",
};

export default Install;
